import { Query, QueryType, SelectQueryOptions, AggregateQueryOptions, LogicalType, LogicalTypeOptions, RowLimit, RowLimitType, SQLDialect, SQLQueryOptions, WindowClause, ColumnRef, WindowFunction, OrderByClause } from '../core/types';
import { DataType } from 'apache-arrow';

interface BuildQueryParams {
  tableName: string;
  query: Query;
}

interface BuildSelectQueryParams {
  tableName: string;
  options: SelectQueryOptions;
}

interface BuildAggregateQueryParams {
  tableName: string;
  options: AggregateQueryOptions;
}

/**
 * Builds SQL queries for DuckDB WASM from our query types
 */
export class DuckDBWasmSQLBuilder {
  /**
   * Build a SQL query from a Query object
   * @performance For aggregate queries with inner limits:
   * - Creates nested subqueries which are expensive to execute
   * - Memory usage increases with query complexity
   * - Performance impact increases with:
   *   - Number of GROUP BY columns
   *   - Number of aggregations
   *   - Size of dataset
   */
  static buildQuery(params: BuildQueryParams): string {
    const { tableName, query } = params;
    switch (query.type) {
      case QueryType.SELECT:
        return this.buildSelectQuery({ tableName, options: query.options as SelectQueryOptions });
      case QueryType.AGGREGATE:
        return this.buildAggregateQuery({ tableName, options: query.options as AggregateQueryOptions });
      case QueryType.SQL:
        return this.buildSQLQuery(query.options as SQLQueryOptions);
      default:
        throw new Error(`Unsupported query type: ${query.type}`);
    }
  }

  /**
   * Build a SELECT query
   */
  private static buildSelectQuery(params: BuildSelectQueryParams): string {
    const { tableName, options } = params;
    if (!options.columns || options.columns.length === 0) {
      // Instead of throwing an error, use SELECT * as a fallback
      console.warn('SQL Builder: Missing columns in SELECT query, using SELECT * as fallback');
      return `SELECT * FROM "${tableName}"`;
    }

    const parts: string[] = [];

    // Check if we have a wildcard column
    const hasWildcard = options.columns.some(col => col.name === '*');
    
    if (hasWildcard) {
      // If we have a wildcard, use SELECT * instead of listing columns
      parts.push(`SELECT *`);
    } else {
      // Normal case - SELECT with column list
      // SELECT clause with type casting and window functions if needed
      const columns = options.columns.map(col => {
        const expr = this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType);
        return `${expr} AS "${col.name}"`;
      });

      // Add window function expressions
      if (options.windows) {
        const windowExprs = options.windows.map(w => this.buildWindowFunction(w));
        columns.push(...windowExprs);
      }

      parts.push(`SELECT ${columns.join(', ')}`);
    }

    // FROM clause with sampling if specified
    let fromClause = `FROM "${tableName}"`;
    if (options.rowLimit?.type === RowLimitType.SAMPLE_PERCENT) {
      fromClause += ` USING SAMPLE ${options.rowLimit.value}%`;
    } else if (options.rowLimit?.type === RowLimitType.SAMPLE_N) {
      fromClause += ` USING SAMPLE ${options.rowLimit.value} ROWS`;
    }
    parts.push(fromClause);

    // WHERE clause
    if (options.where) {
      parts.push(`WHERE ${options.where}`);
    }

    // ORDER BY clause
    if (options.orderBy && options.orderBy.length > 0) {
      const orderBy = options.orderBy.map(o => {
        const expr = this.buildColumnExpression(
          o.column.name,
          o.column.schema.type,
          o.column.schema.logicalType
        );
        return `${expr} ${o.direction}`;
      }).join(', ');
      parts.push(`ORDER BY ${orderBy}`);
    }

    // LIMIT for fixed row limit
    if (options.rowLimit?.type === RowLimitType.FIXED) {
      parts.push(`LIMIT ${options.rowLimit.value}`);
    }

    return parts.join(' ');
  }

  /**
   * Build an AGGREGATE query
   * @performance Note on computational cost:
   * - GROUP BY operations require sorting/hashing
   * - Memory usage scales with number of unique groups
   * - Multiple aggregations increase computation time linearly
   */
  private static buildAggregateQuery(params: BuildAggregateQueryParams): string {
    const { tableName, options } = params;
    if (!options.groupBy || options.groupBy.length === 0) {
      throw new Error('Missing required groupBy columns in AGGREGATE query');
    }

    // For aggregate queries with inner row limits, we need to create a subquery
    if (options.innerRowLimit) {
      return this.buildAggregateQueryWithInnerLimit(params);
    }

    const parts: string[] = [];

    // SELECT clause with group by columns and aggregates
    const groupByCols = options.groupBy.map(col => {
      const expr = this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType);
      return `${expr} AS "${col.name}"`;
    });

    const aggCols = options.aggregates.map(agg => {
      let expr = this.buildColumnExpression(agg.column.name, agg.column.schema.type, agg.column.schema.logicalType);
      
      if (agg.castTo) {
        expr = `CAST(${expr} AS ${this.mapArrowTypeToDuckDB(agg.castTo)})`;
      }

      if (agg.operation === 'COUNT' && agg.column.name === '*') {
        return `COUNT(*) AS "${agg.alias}"`;
      }

      return `${agg.operation}(${expr}) AS "${agg.alias}"`;
    });

    parts.push(`SELECT ${[...groupByCols, ...aggCols].join(', ')}`);

    // FROM clause with sampling if specified
    let fromClause = `FROM "${tableName}"`;
    if (options.rowLimit?.type === RowLimitType.SAMPLE_PERCENT) {
      fromClause += ` USING SAMPLE ${options.rowLimit.value}%`;
    } else if (options.rowLimit?.type === RowLimitType.SAMPLE_N) {
      fromClause += ` USING SAMPLE ${options.rowLimit.value} ROWS`;
    }
    parts.push(fromClause);

    // WHERE clause (pre-aggregation filter)
    if (options.where) {
      parts.push(`WHERE ${options.where}`);
    }

    // GROUP BY clause
    const groupByExprs = options.groupBy.map(col => 
      this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType)
    );
    parts.push(`GROUP BY ${groupByExprs.join(', ')}`);

    // HAVING clause (post-aggregation filter)
    if (options.having) {
      parts.push(`HAVING ${options.having}`);
    }

    // ORDER BY clause
    if (options.orderBy && options.orderBy.length > 0) {
      const orderBy = options.orderBy.map(o => {
        const expr = this.buildColumnExpression(
          o.column.name,
          o.column.schema.type,
          o.column.schema.logicalType
        );
        return `${expr} ${o.direction}`;
      }).join(', ');
      parts.push(`ORDER BY ${orderBy}`);
    }

    // LIMIT for fixed row limit
    if (options.rowLimit?.type === RowLimitType.FIXED) {
      parts.push(`LIMIT ${options.rowLimit.value}`);
    }

    return parts.join(' ');
  }

  /**
   * Build an aggregate query with an inner row limit by creating a subquery
   * @performance Most expensive query type:
   * - Creates nested subqueries
   * - Multiple passes over the data
   * - Memory usage scales with both inner and outer result sets
   * - Can be very expensive with large datasets
   */
  private static buildAggregateQueryWithInnerLimit(params: BuildAggregateQueryParams): string {
    const { tableName, options } = params;
    
    if (!options.innerRowLimit) {
      throw new Error('Inner row limit is required for this method');
    }

    // Build the inner query that applies the row limit
    const innerParts: string[] = [];
    
    // Select all columns needed for aggregation and grouping
    const neededColumns = new Set([
      ...options.groupBy.map(col => col.name),
      ...options.aggregates.map(agg => agg.column.name).filter(name => name !== '*')
    ]);
    
    const innerColumns = Array.from(neededColumns).map(name => `"${name}"`);
    innerParts.push(`SELECT ${innerColumns.join(', ')}`);
    
    // FROM clause with sampling if specified
    let fromClause = `FROM "${tableName}"`;
    if (options.innerRowLimit.type === RowLimitType.SAMPLE_PERCENT) {
      fromClause += ` USING SAMPLE ${options.innerRowLimit.value}%`;
    } else if (options.innerRowLimit.type === RowLimitType.SAMPLE_N) {
      fromClause += ` USING SAMPLE ${options.innerRowLimit.value} ROWS`;
    }
    innerParts.push(fromClause);

    // WHERE clause for inner query
    if (options.where) {
      innerParts.push(`WHERE ${options.where}`);
    }

    // LIMIT for fixed inner row limit
    if (options.innerRowLimit.type === RowLimitType.FIXED) {
      innerParts.push(`LIMIT ${options.innerRowLimit.value}`);
    }

    // Build the outer query that does the aggregation
    const outerParts: string[] = [];

    // SELECT clause with group by columns and aggregates
    const groupByCols = options.groupBy.map(col => {
      const expr = this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType);
      return `${expr} AS "${col.name}"`;
    });

    const aggCols = options.aggregates.map(agg => {
      let expr = agg.column.name === '*' ? '*' : this.buildColumnExpression(agg.column.name, agg.column.schema.type, agg.column.schema.logicalType);
      
      if (agg.castTo) {
        expr = `CAST(${expr} AS ${this.mapArrowTypeToDuckDB(agg.castTo)})`;
      }

      if (agg.operation === 'COUNT' && agg.column.name === '*') {
        return `COUNT(*) AS "${agg.alias}"`;
      }

      return `${agg.operation}(${expr}) AS "${agg.alias}"`;
    });

    outerParts.push(`SELECT ${[...groupByCols, ...aggCols].join(', ')}`);
    outerParts.push(`FROM (${innerParts.join(' ')}) t`);

    // GROUP BY clause
    const groupByExprs = options.groupBy.map(col => 
      this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType)
    );
    outerParts.push(`GROUP BY ${groupByExprs.join(', ')}`);

    // HAVING clause
    if (options.having) {
      outerParts.push(`HAVING ${options.having}`);
    }

    // ORDER BY clause
    if (options.orderBy && options.orderBy.length > 0) {
      const orderBy = options.orderBy.map(o => {
        const expr = this.buildColumnExpression(
          o.column.name,
          o.column.schema.type,
          o.column.schema.logicalType
        );
        return `${expr} ${o.direction}`;
      }).join(', ');
      outerParts.push(`ORDER BY ${orderBy}`);
    }

    // LIMIT for fixed outer row limit
    if (options.rowLimit?.type === RowLimitType.FIXED) {
      outerParts.push(`LIMIT ${options.rowLimit.value}`);
    }

    return outerParts.join(' ');
  }

  /**
   * Build a column expression with appropriate type casting
   */
  private static buildColumnExpression(name: string, _type: DataType, logicalType?: LogicalTypeOptions): string {
    let expr = `"${name}"`;

    // Apply type casting based on logical type
    if (logicalType) {
      switch (logicalType.type) {
        case LogicalType.TIMESTAMP:
          expr = `CAST(${expr} AS TIMESTAMP${logicalType.precision ? `(${logicalType.precision})` : ''})`;
          if (logicalType.timezone) {
            expr = `${expr} AT TIME ZONE '${logicalType.timezone}'`;
          }
          break;
        case LogicalType.DATE:
          expr = `CAST(${expr} AS DATE)`;
          break;
        case LogicalType.CURRENCY:
          expr = `CAST(${expr} AS DECIMAL(${logicalType.precision || 18}, 2))`;
          break;
        case LogicalType.PERCENTAGE:
          expr = `CAST(${expr} AS DECIMAL(5, 2))`;
          break;
      }
    }

    return expr;
  }

  /**
   * Map Arrow data type to DuckDB type
   */
  private static mapArrowTypeToDuckDB(type: DataType): string {
    // This is a simplified mapping, we'll need to expand it
    switch (type.toString()) {
      case 'Int32':
        return 'INTEGER';
      case 'Int64':
        return 'BIGINT';
      case 'Float':
        return 'FLOAT';
      case 'Double':
        return 'DOUBLE';
      case 'Utf8':
        return 'VARCHAR';
      case 'Bool':
        return 'BOOLEAN';
      case 'Date32':
      case 'Date64':
        return 'DATE';
      case 'Time32':
      case 'Time64':
        return 'TIME';
      case 'Timestamp':
        return 'TIMESTAMP';
      default:
        // For unknown types, default to VARCHAR
        console.warn(`Unknown Arrow type: ${type}, defaulting to VARCHAR`);
        return 'VARCHAR';
    }
  }

  /**
   * Build a direct SQL query with dialect validation
   */
  private static buildSQLQuery(options: SQLQueryOptions): string {
    // Validate that the SQL dialect matches DuckDB
    if (options.dialect !== SQLDialect.DUCKDB) {
      throw new Error(`Unsupported SQL dialect: ${options.dialect}. This builder only supports DuckDB dialect.`);
    }

    // If there are parameters, replace them in the SQL
    let sql = options.sql;
    if (options.parameters) {
      Object.entries(options.parameters).forEach(([key, value]) => {
        // Simple parameter replacement - in a real implementation we'd want to properly escape values
        sql = sql.replace(new RegExp(`\\$${key}\\b`, 'g'), this.formatParameterValue(value));
      });
    }

    return sql;
  }

  /**
   * Format a parameter value for SQL injection
   */
  private static formatParameterValue(value: unknown): string {
    if (value === null) {
      return 'NULL';
    }
    if (typeof value === 'string') {
      // Escape single quotes and wrap in quotes
      return `'${value.replace(/'/g, "''")}'`;
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }
    if (value instanceof Date) {
      return `'${value.toISOString()}'`;
    }
    if (Array.isArray(value)) {
      return `(${value.map(v => this.formatParameterValue(v)).join(', ')})`;
    }
    throw new Error(`Unsupported parameter type: ${typeof value}`);
  }

  /**
   * Build a window function expression
   */
  private static buildWindowFunction(window: WindowClause): string {
    // Convert enum to SQL function name
    const fnName = WindowFunction[window.function];
    
    // Build function expression
    let expr: string;
    if (window.column) {
      const colExpr = this.buildColumnExpression(
        window.column.name,
        window.column.schema.type,
        window.column.schema.logicalType
      );
      expr = `${fnName}(${colExpr}`;
      if (window.args && window.args.length > 0) {
        expr += `, ${window.args.map((arg: unknown) => this.formatParameterValue(arg)).join(', ')}`;
      }
      expr += ')';
    } else {
      expr = `${fnName}()`;
    }

    // Build OVER clause
    const overParts: string[] = [];

    if (window.window.partitionBy && window.window.partitionBy.length > 0) {
      const partitionCols = window.window.partitionBy.map((col: ColumnRef) => 
        this.buildColumnExpression(col.name, col.schema.type, col.schema.logicalType)
      );
      overParts.push(`PARTITION BY ${partitionCols.join(', ')}`);
    }

    if (window.window.orderBy && window.window.orderBy.length > 0) {
      const orderBy = window.window.orderBy.map((o: OrderByClause) => {
        const expr = this.buildColumnExpression(
          o.column.name,
          o.column.schema.type,
          o.column.schema.logicalType
        );
        return `${expr} ${o.direction}`;
      }).join(', ');
      overParts.push(`ORDER BY ${orderBy}`);
    }

    if (window.window.frame) {
      const { type, start, end } = window.window.frame;
      let frameExpr = `${type} `;
      
      // Build frame bounds
      if (typeof start === 'number') {
        frameExpr += `${start} PRECEDING`;
      } else {
        frameExpr += start;
      }

      if (end) {
        frameExpr += ' AND ';
        if (typeof end === 'number') {
          frameExpr += `${end} FOLLOWING`;
        } else {
          frameExpr += end;
        }
      }

      overParts.push(frameExpr);
    }

    expr += ` OVER (${overParts.join(' ')})`;
    return `${expr} AS "${window.alias}"`;
  }
} 